import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Home,
  Zap,
  Car,
  Cpu,
  Building2,
  GraduationCap,
  Layers,
  Mail,
  Newspaper,
  Presentation,
  FileText,
  Wine,
  Sun,
  ArrowRight,
  Command as CommandIcon,
  CornerDownLeft,
} from "lucide-react";
import { useHannoContent } from "@/data/hanno-content";

/**
 * Global Command Palette (⌘K / Ctrl+K).
 * Executive-grade site-wide search across pages, ecosystems, projects and presentations.
 */

// Build a searchable index from the current content dictionary.
function buildIndex(c) {
  const nav = c.nav || {};
  const s = c.search || {};
  const g = s.groups || {};

  const eco = (slug) => (c.eco && c.eco[slug]) || {};

  return [
    // Pages
    { id: "home", to: "/", icon: Home, group: g.pages || "Pages",
      title: nav.home || "Home", subtitle: "HannoNexus", keywords: "home início accueil startseite" },
    { id: "projects", to: "/projects", icon: Layers, group: g.pages || "Pages",
      title: nav.projects || "Projects", subtitle: "Portfólio", keywords: "projects projetos projekte projets portfólio portfolio" },
    { id: "contact", to: "/contact", icon: Mail, group: g.pages || "Pages",
      title: nav.contact || "Contact", subtitle: "Strategic Partnerships", keywords: "contact contacto contato kontakt partnerships parceria" },
    { id: "press-kit", to: "/press-kit", icon: Newspaper, group: g.pages || "Pages",
      title: "Press & Investor Kit", subtitle: "Media, logos, factsheets", keywords: "press kit media investors imprensa investidor presse investisseur" },
    { id: "mou", to: "/mou", icon: FileText, group: g.pages || "Pages",
      title: "Memorandum of Understanding", subtitle: "Strategic Partnership · Bluesun Solar",
      keywords: "mou memorandum understanding partnership parceria bluesun solar acordo entendimento cooperation" },
    { id: "mionga", to: "/mionga", icon: Wine, group: g.pages || "Pages",
      title: "MIONGA Beverages", subtitle: "Premium Lifestyle Ecosystem · Germany × Cape Verde",
      keywords: "mionga beverages bebidas boissons getränke water água wasser eau coffee café kaffee moin ola liomona kinderecke hospitality gastronomia gastronomy gastronomie hospitalité alemanha alemão german deutschland cape verde cabo verde hamburg praia lifestyle premium" },
    { id: "neunexus", to: "/neunexus", icon: Zap, group: g.pages || "Pages",
      title: "NeuNexus Energy · Consortium", subtitle: "Uetze Smart Factory + Mobility Hub · Siemens × VCOW × DFOPTIX",
      keywords: "neunexus uetze industry 4.0 siemens vcow dfoptix smart factory mobility hub energiewende energia energy energie germany deutschland niedersachsen calibration robotic vision consortium consórcio pitch investor" },
    { id: "neugreen", to: "/neugreen", icon: Sun, group: g.pages || "Pages",
      title: "NeuGreen Energy · Corporate Pitch", subtitle: "Intelligent Energy Ecosystem Germany · Solar · Storage · Mobility · Digital",
      keywords: "neugreen energy energia energie solar photovoltaic pv fotovoltaico speicher storage armazenamento battery bateria batterie ev mobility mobilidade mobilität ems energy management gestão gestion bluesun wattzing beny germany deutschland alemanha independence unabhängigkeit independência corporate pitch briefing investor investidor installer instalador installateur einspeisemanagement vde tüv" },

    // Ecosystems
    { id: "hx-energy", to: "/hx-energy", icon: Zap, group: g.ecosystems || "Ecosystems",
      title: nav.hx_energy || "HX Energy", subtitle: eco("hx-energy").tagline || "Smart energy ecosystem",
      keywords: "energy energia energie solar bess pulse charge photovoltaic fotovoltaico" },
    { id: "hx-automotive", to: "/hx-automotive", icon: Car, group: g.ecosystems || "Ecosystems",
      title: nav.hx_automotive || "HX Automotive", subtitle: eco("hx-automotive").tagline || "Electric mobility platform",
      keywords: "automotive mobility ev electric vehicle mobilidade mobilité elektro carros veículos" },
    { id: "hx-systems", to: "/hx-systems", icon: Cpu, group: g.ecosystems || "Ecosystems",
      title: nav.hx_systems || "HX Systems", subtitle: eco("hx-systems").tagline || "Digital operating platform",
      keywords: "systems sistemas digital nexis platform plataforma ai iot data telemetria" },
    { id: "hx-infrastructure", to: "/hx-infrastructure", icon: Building2, group: g.ecosystems || "Ecosystems",
      title: nav.hx_infrastructure || "HX Infrastructure", subtitle: eco("hx-infrastructure").tagline || "Physical assets",
      keywords: "infrastructure infraestrutura infrastruktur ativos assets buildings edifícios" },
    { id: "hx-academy", to: "/hx-academy", icon: GraduationCap, group: g.ecosystems || "Ecosystems",
      title: nav.hx_academy || "HX Academy", subtitle: eco("hx-academy").tagline || "Talent & certification",
      keywords: "academy academia formação training ausbildung certificação certificate" },

    // Projects
    { id: "biopintostp", to: "/projects/biopintostp", icon: Layers, group: g.projects || "Projects",
      title: "BioPintoSTP", subtitle: "São Tomé e Príncipe · Agriculture Hub",
      keywords: "biopinto stp são tomé são tome principe agricultura agriculture cacau cocoa" },
    { id: "cabo-verde-hub", to: "/projects/cabo-verde-hub", icon: Layers, group: g.projects || "Projects",
      title: "Cabo Verde Hub", subtitle: "República de Cabo Verde · Energy & Mobility",
      keywords: "cabo verde cape hub praia energy mobility mobilidade" },

    // Presentations
    { id: "blueprint", to: "/blueprint", icon: Presentation, group: g.presentations || "Presentations",
      title: "Agriculture Blueprint", subtitle: "Interactive Slideshow · BioPintoSTP",
      keywords: "blueprint agricultura agriculture apresentação presentation slides são tomé stp" },
    { id: "blueprint-automotive", to: "/blueprint-automotive", icon: Presentation, group: g.presentations || "Presentations",
      title: "Automotive Blueprint", subtitle: "Interactive Slideshow · HX Automotive",
      keywords: "blueprint automotive automóvel automobile slides apresentação presentation ev mobility" },
  ];
}

function scoreItem(item, query) {
  if (!query) return 1;
  const q = query.toLowerCase().trim();
  const hay = `${item.title} ${item.subtitle} ${item.keywords}`.toLowerCase();
  // exact substring match ranks highest
  const idx = hay.indexOf(q);
  if (idx === -1) {
    // fallback: token match
    const tokens = q.split(/\s+/).filter(Boolean);
    const allHit = tokens.every((t) => hay.includes(t));
    return allHit ? 0.3 : 0;
  }
  // earlier match = higher score, title match bonus
  const inTitle = item.title.toLowerCase().includes(q);
  return (inTitle ? 2 : 1) + Math.max(0, 1 - idx / 60);
}

export const CommandPalette = ({ open, onClose }) => {
  const c = useHannoContent();
  const s = c.search || {};
  const navigate = useNavigate();
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);

  const index = React.useMemo(() => buildIndex(c), [c]);

  const filtered = React.useMemo(() => {
    const scored = index
      .map((item) => ({ item, score: scoreItem(item, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.item);
    return scored;
  }, [index, query]);

  // Group results, preserving order
  const grouped = React.useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      if (!map.has(it.group)) map.set(it.group, []);
      map.get(it.group).push(it);
    });
    return Array.from(map.entries());
  }, [filtered]);

  // Flat list for keyboard navigation
  const flat = React.useMemo(() => grouped.flatMap(([, items]) => items), [grouped]);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  React.useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep active item scrolled into view
  React.useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = React.useCallback(
    (item) => {
      onClose?.();
      navigate(item.to);
    },
    [navigate, onClose]
  );

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[active];
      if (item) go(item);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] sm:pt-[14vh]"
          data-testid="command-palette-overlay"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl rounded-2xl overflow-hidden border border-white/[0.10] bg-[#0A0F1A]/95 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
            style={{ boxShadow: "0 40px 120px -20px rgba(0,0,0,0.9), 0 0 0 1px rgba(10,155,95,0.06)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="command-palette"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.10]">
              <Search size={18} className="text-hx-neon shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={s.placeholder || "Search…"}
                data-testid="command-palette-input"
                className="flex-1 bg-transparent outline-none border-none text-hx-text placeholder:text-hx-muted/50 text-[15px] font-medium tracking-tight"
                autoComplete="off"
                spellCheck={false}
              />
              <button
                onClick={onClose}
                className="text-[10px] uppercase tracking-[0.18em] text-hx-muted/70 hover:text-hx-text px-2 py-1 rounded-md border border-white/[0.10] hover:border-white/[0.10] transition"
                data-testid="command-palette-close"
              >
                ESC
              </button>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-2">
              {grouped.length === 0 ? (
                <div className="px-6 py-14 text-center">
                  <div className="text-hx-muted/60 text-sm">{s.empty || "No results"}</div>
                </div>
              ) : (
                grouped.map(([group, items]) => (
                  <div key={group} className="pb-2">
                    <div className="px-5 pt-3 pb-1.5 text-[9px] uppercase tracking-[0.24em] text-hx-muted/50 font-semibold">
                      {group}
                    </div>
                    <ul>
                      {items.map((item) => {
                        const idx = flat.indexOf(item);
                        const isActive = idx === active;
                        const Icon = item.icon || Search;
                        return (
                          <li key={item.id}>
                            <button
                              type="button"
                              data-idx={idx}
                              data-testid={`command-item-${item.id}`}
                              onMouseEnter={() => setActive(idx)}
                              onClick={() => go(item)}
                              className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                                isActive
                                  ? "bg-hx-neon/[0.10] text-hx-text"
                                  : "text-hx-muted hover:text-hx-text"
                              }`}
                            >
                              <div
                                className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                                  isActive ? "bg-hx-neon/[0.16] text-hx-neon" : "bg-white/[0.03] text-hx-muted"
                                }`}
                              >
                                <Icon size={15} strokeWidth={2} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-medium tracking-tight truncate text-hx-text">
                                  {item.title}
                                </div>
                                <div className="text-[11px] text-hx-muted/70 truncate">
                                  {item.subtitle}
                                </div>
                              </div>
                              <ArrowRight
                                size={14}
                                className={`shrink-0 transition-opacity ${
                                  isActive ? "opacity-100 text-hx-neon" : "opacity-0"
                                }`}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>

            {/* Footer hints */}
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-white/[0.10] bg-black/40">
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.18em] text-hx-muted/70">
                <span className="inline-flex items-center gap-1.5">
                  <Kbd>↑</Kbd><Kbd>↓</Kbd>{s.hint_navigate || "Navigate"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Kbd><CornerDownLeft size={9} strokeWidth={2.4} /></Kbd>{s.hint_select || "Open"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Kbd>ESC</Kbd>{s.hint_close || "Close"}
                </span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted/50 hidden sm:flex items-center gap-1.5">
                <CommandIcon size={10} /> HannoNexus
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const Kbd = ({ children }) => (
  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded border border-white/[0.10] bg-white/[0.03] text-[9px] font-semibold text-hx-muted">
    {children}
  </span>
);

/** Hook: opens the palette on ⌘K / Ctrl+K / "/" (when not in an input). */
export function useCommandPaletteShortcut(setOpen) {
  React.useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "/" ) {
        const tag = (e.target && e.target.tagName) || "";
        const isTyping = tag === "INPUT" || tag === "TEXTAREA" || (e.target && e.target.isContentEditable);
        if (!isTyping) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

export default CommandPalette;
